import React from 'react';
import { perMachineSummarySalesData, perMachineTotalSalesData } from '@dataTypes/perMachineTypes';
import { format, subDays } from 'date-fns';
import { Button, Form } from 'react-bootstrap';
import {
  isSharedCatId,
  SHARED_CATID_C2_WARNING_MESSAGE,
  SHARED_CATID_S0_BLOCKED_MESSAGE,
} from './sharedCatIdMessages';

/** 단말기 WebSocket 상태 (usage_status, 연결 여부, 재고/재고관리, C0 Version) */
export type TerminalStatus = {
  usageStatus?: string;
  isConnected?: boolean;
  /** 동일 catId 연결 세션 수 (2 이상이면 공유) */
  connectedSessionCount?: number | null;
  stock?: number | null;
  stockManagementOn?: boolean | null;
  /** 정보알림(C0) 펌웨어 버전 (예: V0001) */
  version?: string | null;
  /** 서버 기록 FW 자동업데이트: 01=사용, 00=사용안함 */
  fwAutoUpdate?: string | null;
  /** C2(06) 서버 기록 인터넷 망 기능: 01=On, 00=Off */
  internetNetwork?: string | null;
  /** C2(10) 서버 기록 밝기 1~10 */
  displayBrightness?: number | null;
};

function formatFwAutoUpdateLabel(fw?: string | null): string {
  if (fw === '01') return '켜짐';
  if (fw === '00') return '꺼짐';
  return '-';
}

function formatInternetNetworkLabel(v?: string | null): string {
  if (v === '01') return 'On';
  if (v === '00') return 'Off';
  return '-';
}

function formatBrightnessLabel(n?: number | null): string {
  if (n != null && n >= 1 && n <= 10) return String(n);
  return '-';
}

function formatUsageStatus(usageStatus?: string): string {
  if (usageStatus === '00') return '카드·현금 가능';
  if (usageStatus === '01') return '사용불가(점검중)';
  if (usageStatus === '02') return '현금만 가능';
  return '-';
}

interface PerMachineItemProps {
  item: perMachineTotalSalesData | perMachineSummarySalesData;
  /** false이면 마일리지(현금) 행 숨김 */
  showMileage?: boolean;
  /** 단말기 재고 (GET /api/stock). 없으면 null */
  stock?: number | null;
  /** 단말기 WebSocket 상태 (usage_status, 연결 여부). 없으면 조회 안 됨 */
  terminalStatus?: TerminalStatus;
  handleServiceCoinModalShow: (
    machineData: perMachineTotalSalesData | perMachineSummarySalesData,
  ) => void;
  handlePaymentHistoryModalShow: (
    machineData: perMachineTotalSalesData | perMachineSummarySalesData,
  ) => void;
  handleRemoteControlModalShow: (
    machineData: perMachineTotalSalesData | perMachineSummarySalesData,
  ) => void;
  isSelected: boolean;
  onToggleSelect: (catId: string) => void;
}

const PerMachineItem = ({
  item,
  showMileage = true,
  stock = null,
  terminalStatus,
  handleServiceCoinModalShow,
  handlePaymentHistoryModalShow,
  handleRemoteControlModalShow,
  isSelected,
  onToggleSelect,
}: PerMachineItemProps) => {
  const today = new Date((item as perMachineTotalSalesData).date || new Date());
  const yesterday = subDays(today, 1);
  const formattedYesterday = format(yesterday, 'MM-dd');
  const twoDaysAgo = subDays(today, 2);
  const formattedTwoDaysAgo = format(twoDaysAgo, 'MM-dd');
  const totalSales = item.card + item.cash + item.etc;
  const isConnected = terminalStatus?.isConnected;
  const sharedCatId = isSharedCatId(terminalStatus?.connectedSessionCount);
  const usageStatusLabel = formatUsageStatus(terminalStatus?.usageStatus);

  const handleServiceCoinClick = () => {
    if (sharedCatId) {
      window.alert(SHARED_CATID_S0_BLOCKED_MESSAGE);
      return;
    }
    handleServiceCoinModalShow(item);
  };

  const handlePaymentHistoryClick = () => {
    handlePaymentHistoryModalShow(item);
  };

  const handleRemoteControlClick = () => {
    handleRemoteControlModalShow(item);
  };

  return (
    <div className={`item${isSelected ? ' item--selected' : ''}`}>
      <section className="top_section">
        <div className="store_name_wrapper">
          <Form.Check
            type="checkbox"
            className="per_machine_row_check"
            checked={isSelected}
            onChange={() => onToggleSelect(item.catId)}
            aria-label={`${item.deviceAlias} 선택`}
            id={`per-machine-select-${String(item.catId).replace(/[^a-zA-Z0-9_-]/g, '_')}`}
          />
          <span className="store_order">{item.sortOrder}.</span>
          <span className="storeName">{item.deviceAlias}</span>
          {terminalStatus != null && (
            <>
              <span
                className="terminal_status_badge usage_status"
                title="단말기 사용상태 (usage_status)"
              >
                {usageStatusLabel}
              </span>
              <span
                className={`terminal_status_badge connection ${isConnected ? 'connected' : 'disconnected'}`}
                title={isConnected ? 'WebSocket 연결성공' : 'WebSocket 연결실패'}
              >
                {isConnected ? '🟢 연결성공' : '🔴 연결실패'}
              </span>
              <span className="terminal_status_badge version_badge" title="정보알림(C0) 단말 버전">
                버전 {terminalStatus.version?.trim() ? terminalStatus.version.trim() : '-'}
              </span>
              <span
                className="terminal_status_badge auto_update_badge"
                title="자동 업데이트 사용 여부(원격 자동 업데이트 설정 전송 후 서버 기록)"
              >
                자동업데이트 {formatFwAutoUpdateLabel(terminalStatus.fwAutoUpdate)}
              </span>
              <span
                className="terminal_status_badge"
                title="인터넷 망 기능(C2·06 서버 기록 On/Off)"
              >
                인터넷망 {formatInternetNetworkLabel(terminalStatus.internetNetwork)}
              </span>
              <span className="terminal_status_badge" title="디스플레이 밝기(C2·10 서버 기록 1~10)">
                밝기 {formatBrightnessLabel(terminalStatus.displayBrightness)}
              </span>
            </>
          )}
        </div>
        <div className="service_coin_container">
          <Button className="payment_history_button" onClick={handlePaymentHistoryClick}>
            결제 내역
          </Button>
          <Button
            className="service_coin_button"
            onClick={handleServiceCoinClick}
            disabled={sharedCatId}
            title={sharedCatId ? SHARED_CATID_S0_BLOCKED_MESSAGE : undefined}
          >
            서비스 지급
          </Button>
          <Button
            className="service_coin_button remote_control_button"
            onClick={handleRemoteControlClick}
            title={
              sharedCatId
                ? SHARED_CATID_C2_WARNING_MESSAGE
                : isConnected === false
                  ? '단말기가 연결되어 있지 않습니다. 연결 후 사용하세요.'
                  : '재부팅, 사용설정, 재고설정, 자동 업데이트, 단말기 업데이트, TMS 로그 요청(05), 인터넷 망 기능, 밝기'
            }
          >
            원격 관리
          </Button>
        </div>
      </section>

      <section className="bottom_section">
        <div className="totalPrice_wrapper">
          <span className="title">총 매출</span>
          <span className="totalPrice">{totalSales.toLocaleString('ko-KR')}</span>

          <div className="sub_price_wrapper">
            <div className="price payCard">
              <div className="price_wrapper">
                <span className="text">카드</span>
                <span className="money">{item.card.toLocaleString('ko-KR')}</span>
              </div>
              {item.cardCnt > 0 && <span className="count">({item.cardCnt}건)</span>}
            </div>

            {showMileage && (
              <div className="price payCash">
                <div className="price_wrapper">
                  <span className="text">마일리지</span>
                  <span className="money">{item.cash.toLocaleString('ko-KR')}</span>
                </div>
                {item.cashCnt > 0 && <span className="count">({item.cashCnt}건)</span>}
              </div>
            )}

            <div className="price payEtc">
              <div className="price_wrapper">
                <span className="text">기타</span>
                <span className="money">{item.etc.toLocaleString('ko-KR')}</span>
              </div>
              {item.etcCnt > 0 && <span className="count">({item.etcCnt}건)</span>}
            </div>
          </div>
        </div>

        <div className="past_price_wrapper">
          {'oneDay' in item && 'twoDay' in item && 'date' in item && (
            <>
              <div className="past">
                <span className="date">{formattedTwoDaysAgo}</span>
                <span className="price">{item.twoDay.toLocaleString('ko-KR')}</span>
              </div>

              <div className="past">
                <span className="date">{formattedYesterday}</span>
                <span className="price">{item.oneDay.toLocaleString('ko-KR')}</span>
              </div>
            </>
          )}
          <div className="service_coin_wrapper">
            <span className="service_coin_title">재고량</span>
            <span className="service_coin_value">
              {stock != null ? stock.toLocaleString('ko-KR') : '-'}
            </span>
            {terminalStatus?.stockManagementOn != null && (
              <span className="stock_management_status">
                (재고관리 {terminalStatus.stockManagementOn ? 'On' : 'Off'})
              </span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PerMachineItem;
